import React, { useEffect, useMemo, useState } from 'react';
import toast from 'react-hot-toast';
import { serviceFeeApi } from '../../api/serviceFeeApi';
import { generateBillingPeriods } from '../../utils/billingPeriods';
import {
  formatCasualParkingDateTime,
  parseCasualParkingDateTimeInput,
} from '../../utils/casualParkingInvoice';
import { parseContractAmountField } from '../../utils/contractAmount.util';
import { formatVndInput } from '../../utils/vndFormat';
import { getApiErrorMessage } from '../../utils/apiErrors';
import {
  buildCasualParkingVariancePayloadLines,
  CasualParkingVarianceLinesEditor,
  emptyCasualParkingVarianceLine,
  type CasualParkingVarianceLineForm,
  validateCasualParkingVariancePartnerSelection,
} from './CasualParkingVarianceLinesEditor';
import { apartmentsApi } from '../../api/apartmentsApi';
import type { CasualParkingFeeInitialValues } from '../../utils/legacyShiftCasualParking';

interface AddCasualParkingFeeModalProps {
  apartmentId?: string;
  buildingId: string;
  isOpen: boolean;
  onClose: () => void;
  onSuccess?: () => void;
  initialValues?: CasualParkingFeeInitialValues;
}

export const AddCasualParkingFeeModal: React.FC<AddCasualParkingFeeModalProps> = ({
  apartmentId,
  buildingId,
  isOpen,
  onClose,
  onSuccess,
  initialValues,
}) => {
  const [selectedApartmentId, setSelectedApartmentId] = useState('');
  const [apartmentOptions, setApartmentOptions] = useState<Array<{ id: string; code: string }>>(
    [],
  );
  const [billingPeriod, setBillingPeriod] = useState('');
  const [revenueFrom, setRevenueFrom] = useState('');
  const [revenueTo, setRevenueTo] = useState('');
  const [machineAmount, setMachineAmount] = useState('');
  const [actualAmount, setActualAmount] = useState('');
  const [notes, setNotes] = useState('');
  const [varianceLines, setVarianceLines] = useState<CasualParkingVarianceLineForm[]>([]);
  const [saving, setSaving] = useState(false);
  const billingPeriods = generateBillingPeriods();

  const resolvedApartmentId = apartmentId ?? selectedApartmentId;

  useEffect(() => {
    if (!isOpen || apartmentId || !buildingId) return;
    apartmentsApi
      .getApartments({ buildingId, limit: 500 })
      .then((rows) =>
        setApartmentOptions(
          rows.map((a) => ({ id: a.id, code: a.code })).sort((a, b) => a.code.localeCompare(b.code)),
        ),
      )
      .catch(() => setApartmentOptions([]));
  }, [isOpen, apartmentId, buildingId]);

  useEffect(() => {
    if (!isOpen) return;
    const now = new Date();
    setRevenueFrom(initialValues?.revenueFrom ?? formatCasualParkingDateTime(now));
    setRevenueTo(initialValues?.revenueTo ?? formatCasualParkingDateTime(now));
    setBillingPeriod(initialValues?.billingPeriod ?? billingPeriods[0]?.value ?? '');
    setMachineAmount(initialValues?.machineAmount ?? '');
    setActualAmount(initialValues?.actualAmount ?? '');
    setNotes(initialValues?.notes ?? '');
    setVarianceLines([]);
    if (!apartmentId) {
      setSelectedApartmentId('');
    }
  }, [isOpen, apartmentId, initialValues]);

  const machineNum = parseContractAmountField(machineAmount);
  const actualNum = parseContractAmountField(actualAmount);
  const varianceTotal = varianceLines.reduce(
    (sum, line) => sum + (parseContractAmountField(line.amount) || 0),
    0,
  );
  const expectedVariance = Math.max(0, machineNum - actualNum);
  const varianceRemaining = expectedVariance - varianceTotal;

  const showVarianceSection = useMemo(
    () => machineNum > 0 && actualNum >= 0 && machineNum > actualNum,
    [machineNum, actualNum],
  );

  useEffect(() => {
    if (!showVarianceSection) {
      setVarianceLines([]);
    } else if (varianceLines.length === 0) {
      setVarianceLines([emptyCasualParkingVarianceLine()]);
    }
  }, [showVarianceSection]);

  if (!isOpen) return null;

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!resolvedApartmentId) {
      toast.error('Vui lòng chọn căn hộ ghi nhận doanh thu xe vãng lai');
      return;
    }
    const parsedMachineAmount = parseContractAmountField(machineAmount);
    const parsedActualAmount = parseContractAmountField(actualAmount);
    if (!billingPeriod || !revenueFrom || !revenueTo || !parsedActualAmount) {
      toast.error('Vui lòng điền kỳ hóa đơn, thời gian và số tiền thực thu');
      return;
    }
    const parsedFrom = parseCasualParkingDateTimeInput(revenueFrom);
    const parsedTo = parseCasualParkingDateTimeInput(revenueTo);
    if (!parsedFrom || !parsedTo) {
      toast.error('Ngày tính doanh thu phải theo định dạng dd/mm/yyyy HH:mm');
      return;
    }
    if (parsedTo.getTime() <= parsedFrom.getTime()) {
      toast.error('Thời gian kết thúc phải sau thời gian bắt đầu');
      return;
    }

    if (parsedMachineAmount > 0 && parsedMachineAmount !== parsedActualAmount + varianceTotal) {
      toast.error(
        `Chênh lệch còn ${formatVndInput(String(Math.max(0, parsedMachineAmount - parsedActualAmount - varianceTotal)))}đ — hãy phân bổ Khấu trừ / Voucher`,
      );
      return;
    }

    const payloadLines = buildCasualParkingVariancePayloadLines(varianceLines);

    if (showVarianceSection && payloadLines.length === 0) {
      toast.error('Vui lòng thêm ít nhất một dòng Khấu trừ hoặc Voucher');
      return;
    }

    const partnersRes = await serviceFeeApi.getCasualParkingVoucherContractTargets(buildingId, '');
    const voucherPartnerOptions = (partnersRes.partners ?? []).map((p) => ({
      optionKey: p.optionKey,
      contractCode: p.targets[0]?.contractCode ?? '',
      counterpartyName: p.counterpartyName,
      displayLabel: p.displayLabel,
      targets: p.targets.map((t) => ({
        contractApartmentId: t.contractApartmentId,
        contractCode: t.contractCode,
        apartmentCode: t.apartmentCode,
        customerName: t.customerName,
      })),
    }));
    const partnerError = validateCasualParkingVariancePartnerSelection(
      varianceLines,
      voucherPartnerOptions,
    );
    if (partnerError) {
      toast.error(partnerError);
      return;
    }

    setSaving(true);
    try {
      await serviceFeeApi.createCasualParkingFee({
        apartmentId: resolvedApartmentId,
        billingPeriod,
        revenueFrom: parsedFrom.toISOString(),
        revenueTo: parsedTo.toISOString(),
        machineAmount: parsedMachineAmount,
        actualAmount: parsedActualAmount,
        notes: notes || undefined,
        varianceLines: payloadLines.length ? payloadLines : undefined,
      });
      toast.success('Đã tạo hóa đơn xe vãng lai');
      onSuccess?.();
      onClose();
    } catch (err: unknown) {
      toast.error(getApiErrorMessage(err, 'Không thể tạo hóa đơn'));
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="fixed inset-0 z-[60] overflow-y-auto bg-black/40 p-4">
      <div className="flex min-h-full items-start justify-center py-6">
        <div className="bg-white rounded-lg shadow-xl w-full max-w-2xl p-6">
          <h2 className="text-lg font-bold mb-4">Thêm hóa đơn — Thu tiền xe vãng lai</h2>
          <form onSubmit={handleSubmit} className="space-y-3">
            {!apartmentId && (
              <div>
                <label className="block text-sm font-medium mb-1">Căn hộ ghi nhận *</label>
                <select
                  className="w-full border rounded px-3 py-2"
                  value={selectedApartmentId}
                  onChange={(e) => setSelectedApartmentId(e.target.value)}
                >
                  <option value="">Chọn căn hộ</option>
                  {apartmentOptions.map((apt) => (
                    <option key={apt.id} value={apt.id}>
                      {apt.code}
                    </option>
                  ))}
                </select>
                <p className="mt-1 text-xs text-gray-500">
                  Cùng quy trình Thanh toán phí dịch vụ → Thêm hóa đơn → Thu tiền xe vãng lai.
                </p>
              </div>
            )}
            <div>
              <label className="block text-sm font-medium mb-1">Kỳ hóa đơn *</label>
              <select
                className="w-full border rounded px-3 py-2"
                value={billingPeriod}
                onChange={(e) => setBillingPeriod(e.target.value)}
              >
                {billingPeriods.map((period) => (
                  <option key={period.value} value={period.value}>
                    {period.label}
                  </option>
                ))}
              </select>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
              <div>
                <label className="block text-sm font-medium mb-1">Ngày tính doanh thu từ</label>
                <input
                  type="text"
                  placeholder="dd/mm/yyyy HH:mm"
                  className="w-full border rounded px-3 py-2"
                  value={revenueFrom}
                  onChange={(e) => setRevenueFrom(e.target.value)}
                />
              </div>
              <div>
                <label className="block text-sm font-medium mb-1">Đến</label>
                <input
                  type="text"
                  placeholder="dd/mm/yyyy HH:mm"
                  className="w-full border rounded px-3 py-2"
                  value={revenueTo}
                  onChange={(e) => setRevenueTo(e.target.value)}
                />
              </div>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
              <div>
                <label className="block text-sm font-medium mb-1">Số tiền trên máy giữ xe</label>
                <input
                  type="text"
                  inputMode="numeric"
                  className="w-full border rounded px-3 py-2"
                  value={machineAmount}
                  onChange={(e) => setMachineAmount(formatVndInput(e.target.value))}
                />
              </div>
              <div>
                <label className="block text-sm font-medium mb-1">Số tiền thực thu *</label>
                <input
                  type="text"
                  inputMode="numeric"
                  className="w-full border rounded px-3 py-2"
                  value={actualAmount}
                  onChange={(e) => setActualAmount(formatVndInput(e.target.value))}
                />
              </div>
            </div>

            {showVarianceSection && buildingId ? (
              <CasualParkingVarianceLinesEditor
                buildingId={buildingId}
                billingPeriod={billingPeriod}
                varianceLines={varianceLines}
                setVarianceLines={setVarianceLines}
                expectedVariance={expectedVariance}
                varianceRemaining={varianceRemaining}
                active={isOpen}
              />
            ) : null}

            <div>
              <label className="block text-sm font-medium mb-1">Ghi chú</label>
              <textarea
                className="w-full border rounded px-3 py-2"
                rows={2}
                value={notes}
                onChange={(e) => setNotes(e.target.value)}
              />
            </div>
            <div className="flex justify-end gap-2 pt-2">
              <button type="button" onClick={onClose} className="px-4 py-2 border rounded">
                Hủy
              </button>
              <button
                type="submit"
                disabled={saving}
                className="px-4 py-2 bg-cyan-600 text-white rounded disabled:opacity-50"
              >
                {saving ? 'Đang lưu...' : 'Tạo hóa đơn'}
              </button>
            </div>
          </form>
        </div>
      </div>
    </div>
  );
};

export default AddCasualParkingFeeModal;
